import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ToastController } from "ionic-angular";
import { FacilityCount } from "models/facilities/facility-details";
import { careTypeFromString } from "models/patient-chart/care-type";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { ContactDetails } from "../../models/contact/contactDetails";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import Facility from "../../models/facilities/facility";
import { Patient } from "../../models/patient-chart/patient";
import PatientTeam from "../../models/patient-chart/patient-team";
import { PatientChartResponse } from "../../models/patient-chart/patientChartResponse";
import { ReAdmitRequest } from "../../models/patient-chart/request-models";
import { ConfigurationService } from "../configuration/configuration.service";
import { ReferralsServiceProvider } from "./referrals-service";

@Injectable()
export class PatientChartService {
  chartUrl: string;
  PATIENT: Patient;
  private lookupCache$: Observable<any>;
  PATIENT_CHART: PatientChart;

  constructor(
    public http: HttpClient,
    private toastCtrl: ToastController,
    private config: ConfigurationService
  ) {
    this.chartUrl = this.config.resolvePatientChartsUrl();
  }

  checkDuplicateUniqueIdentifier(
    uniqueIdentifier: string
  ): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.chartUrl}/patients/exists?checkUnique=true`,
      { uniqueIdentifier: uniqueIdentifier }
    );
  }

  checkDuplicatePatient(
    patientId: number,
    firstName: string,
    lastName: string,
    birthDate: string,
    uniqueIdentifier: string
  ): Observable<boolean> {
    return this.http.post<boolean>(`${this.chartUrl}/patients/exists`, {
      patientId: patientId,
      firstName: firstName,
      lastName: lastName,
      birthDate: birthDate,
      uniqueIdentifier: uniqueIdentifier,
    });
  }

  /* Return all available lookup values */
  getLookUpValues() {
    if (!this.lookupCache$) {
      this.lookupCache$ = this.http
        .get(`${this.chartUrl}/patient-charts/lookups`)
        .pipe(shareReplay(1));
    }
    return this.lookupCache$;
  }

  createOrUpdatePatientChart(
    id: number,
    patientChart: any,
    isOEToggleOn: boolean
  ) {
    if (id > 0) {
      return this.http.put(
        `${this.chartUrl}/patient-charts/${id}?isOEToggleOn=${isOEToggleOn}`,
        patientChart
      );
    }
    this.setDocumentCompletion(patientChart);
    patientChart.patientStatus =
      patientChart.careType == "EVALUATION" ? "Evaluation" : "Referral";
    return this.http.post(`${this.chartUrl}/patient-charts`, patientChart);
  }
  newCreateOrUpdatePatientChart(
    id: number,
    patientChart: any,
    isOEToggleOn: boolean
  ) {
    if (id > 0) {
      return this.http.put(
        `${this.chartUrl}/patient-charts/${id}?isOEToggleOn=${isOEToggleOn}`,
        patientChart
      );
    }
    this.setDocumentCompletion(patientChart);
    return this.http.post(`${this.chartUrl}/patient-charts`, patientChart);
  }

  newPatientCareDirectives(
    id: number,
    patientChart: any,
    isOEToggleOn: boolean
  ) {
    if (id > 0) {
      return this.http.put(
        `${this.chartUrl}/patient-care-directives/${id}?isOEToggleOn=${isOEToggleOn}`,
        patientChart
      );
    }
  }

  createPatientChartForPatient(
    patientId: number,
    payload: ReAdmitRequest
  ): Observable<PatientChartResponse> {
    this.setDocumentCompletion(payload);
    return this.http.post<PatientChartResponse>(
      `${this.chartUrl}/patients/${patientId}/patient-charts`,
      payload
    );
  }

  getActivePatientChartForPatient(patientId: number): Observable<any> {
    return this.http.get(
      `${this.chartUrl}/patients/${patientId}/patient-charts`
    );
  }

  getAllPatientChartsForPatient(patientId: number): Observable<any> {
    return this.http.get(
      `${this.chartUrl}/patients/${patientId}/patient-charts?activeOnly=false`
    );
  }

  getPatientById(id, isExternalId: boolean = false): Observable<any> {
    let params = new HttpParams();
    params = params.append("externalId", isExternalId.toString());
    return this.http.get(`${this.chartUrl}/patient-charts/${id}`, {
      params: params,
    });
  }

  searchReferralLocationByType(
    query: string,
    id: string,
    page: number = 0,
    size: number = 10
  ): Observable<ContentWrapper<Facility>> {
    return this.http
      .get<ContentWrapper<any>>(
        `${this.chartUrl}/patient-charts/referral-addresses?page=${page}&limit=${size}&q=${query}&referralTypeId=${id}`
      )
      .map((x) => {
        let wrapper = new ContentWrapper<Facility>();
        wrapper.content = x.content.map((y) => {
          let f = new Facility();
          //f.id = y.id;
          f.name = y.name;
          // f.facilityType = y.facilityType;
          f.address = new ContactDetails(
            y.address1,
            "",
            y.city,
            y.state,
            y.zip,
            y.zipExtension,
            y.phone,
            y.emailAddress,
            y.emailAddress,
            y.county,
            y.fax
          );
          return f;
        });
        return wrapper;
      });
  }

  getPatientChartByQbosId(patientchartId: number): Observable<any> {
    let url =
      this.chartUrl + "/patient-charts/" + patientchartId + "?externalId=true";

    return new Observable((observer) => {
      this.http.get(url).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  getPatientsByTeamSearch(
    teamName: string,
    lastName: string,
    page: number = 0,
    size: number = 10
  ): Observable<ContentWrapper<PatientTeam>> {
    let url = `${this.chartUrl}/patients?q=${lastName}&team-name=${teamName}&page=${page}&limit=${size}`;
    return this.http.get<ContentWrapper<PatientTeam>>(url);
  }

  getPatientsByParams(
    queryTerm: string = "",
    page: number = 0,
    limit: number = 10,
    by: string = "lastName",
    direction: string = "asc",
    status: string = "All",
    eligibility: string = "All",
    careType: string = "All"
  ): Observable<any> {
    let url = `${this.chartUrl}/patient-charts`;
    let params = new HttpParams();
    params = params.append("q", queryTerm);
    params = params.append("page", page.toString());
    params = params.append("limit", limit.toString());
    params = params.append("by", by);
    params = params.append("direction", direction);
    params = params.append("status", status);
    params = params.append("eligibility", eligibility);
    params = params.append("careType", careType);
    return this.http.get(url, { params: params });
  }

  getFacilityCountByPatient(
    facilityId: number,
    deleteFacility: boolean
  ): Observable<any> {
    return this.http.get<FacilityCount>(
      `${this.chartUrl}/patient-charts/patient-count-by-facility/${facilityId}?includeInactiveLOC=${deleteFacility}`
    );
  }

  setDocumentCompletion(patientChart: any) {
    if (!patientChart.careType) {
      return;
    }
    const careType = careTypeFromString(patientChart.careType);
    patientChart.documentCompletion = `0/${
      ReferralsServiceProvider.getConsentTypes(careType, true).length
    }`;
  }

  faxPatientCharts(faxData: any): Observable<any> {
    const url = `${this.chartUrl}/patient-charts/fax`;
    return this.http.post(url, faxData);
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/patient-chart.service.ts